/**
 * A correção do crédito pelo painel, sem tela (E203.F4, ADR-0146 §7; E207.F7.C01, owner, 01/11):
 * os campos que cada linha da conferência imprime, o que a pessoa digitou no alvo, o que mudou em
 * relação ao lido, o que falta para salvar e a gravação — uma revisão `credito` por campo que
 * mudou (com a fonte e o fundamento) e, na linha da conferência de identidade, a revisão `linha`
 * quando o resultado difere do impresso ou quando nenhum valor lido mudou (a nota do perito na
 * linha). Com um valor corrigido e o resultado como está, a conferência refaz o resultado sozinha.
 */
import { ptBR } from '@/i18n/pt-BR';
import {
  chaveDoCredito,
  type CampoDoCreditoLido,
  type CreditoNoPainelData,
  type ResultadoDaLinha,
} from '@/lib/revisaoDoDado';

import { faltaNoFundamento } from './Fundamento';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado;

const CAMPOS_DA_LINHA: Record<string, readonly CampoDoCreditoLido[]> = {
  conta: ['banco', 'agencia', 'conta', 'documento'],
  titular: ['titular'],
  valor: ['valor'],
  data: ['data'],
};

export type Valores = Partial<Record<CampoDoCreditoLido, string>>;

/** De onde o perito tirou o valor: o documento e a página (a página como digitada). */
export interface FonteDoValor {
  documentoId: string;
  pagina: string;
}

/** O que a pessoa digitou nos campos do alvo; muda de alvo, começa do lido do novo alvo. */
export interface Edicao {
  alvo: string;
  valores: Valores;
  fonte: FonteDoValor;
}

/** O alvo escolhido (E203.F4.C, F-E203.F4-06) e a edição dele, como o formulário os guarda. */
export interface EdicaoDoAlvo {
  escolhido: string;
  setEscolhido: (posicao: string) => void;
  alvo: CreditoNoPainelData | undefined;
  atual: Edicao | null;
  setEdicao: (edicao: Edicao) => void;
}

export interface Gravavel {
  mudados: readonly CampoDoCreditoLido[];
  gravaALinha: boolean;
}

/** Os campos lidos que a linha clicada imprime (a conta de destino, o titular, o valor, a data). */
export function camposDaLinha(chave: string): readonly CampoDoCreditoLido[] {
  return CAMPOS_DA_LINHA[chave] ?? CAMPOS_DA_LINHA.conta ?? [];
}

export function chaveDoAlvo(alvo: CreditoNoPainelData): string {
  return `${alvo.documento_id}:${alvo.pagina}`;
}

/** A edição que começa do lido do alvo. */
export function edicaoInicial(
  alvo: CreditoNoPainelData,
  campos: readonly CampoDoCreditoLido[],
): Edicao {
  return {
    alvo: chaveDoAlvo(alvo),
    valores: Object.fromEntries(campos.map((c) => [c, alvo[c] ?? ''])),
    fonte: { documentoId: alvo.documento_id, pagina: String(alvo.pagina) },
  };
}

/** Os campos cujo valor digitado difere do lido do alvo. */
export function camposMudados(
  alvo: CreditoNoPainelData,
  campos: readonly CampoDoCreditoLido[],
  valores: Valores,
): CampoDoCreditoLido[] {
  return campos.filter((c) => (valores[c] ?? '').trim() !== (alvo[c] ?? ''));
}

/** Os campos lidos que mudaram e se a linha vai gravada (o resultado mudou, ou é só ela). */
export function oQueGrava(p: {
  corrigindo: boolean;
  edicao: EdicaoDoAlvo;
  campos: readonly CampoDoCreditoLido[];
  peloResultado: boolean;
  resultado: ResultadoDaLinha | '';
  impresso: ResultadoDaLinha | '';
}): Gravavel {
  const { alvo, atual } = p.edicao;
  const mudados =
    p.corrigindo && alvo !== undefined && atual !== null
      ? camposMudados(alvo, p.campos, atual.valores)
      : [];
  const gravaALinha =
    p.peloResultado && p.resultado !== '' && (p.resultado !== p.impresso || mudados.length === 0);
  return { mudados, gravaALinha };
}

/** A frase do que falta para salvar, ou `null` quando dá para gravar. */
export function oQueFalta(
  p: Gravavel & {
    peloResultado: boolean;
    semAlvo: boolean;
    pagina: string | undefined;
    fundamento: string;
  },
): string | null {
  if (!p.gravaALinha && p.mudados.length === 0) {
    if (p.semAlvo) return t.credito.escolhaAntes;
    return p.peloResultado ? t.falta.resultadoOuValor : t.falta.valor;
  }
  if (p.mudados.length > 0 && !(Number(p.pagina) >= 1)) return t.falta.pagina;
  return faltaNoFundamento(p.fundamento);
}

/** Grava uma revisão por campo que mudou; a primeira recusa pela versão interrompe e volta. */
export async function gravarCorrecoesDoCredito(
  props: Pick<PropsDoConteudo, 'client' | 'caseId' | 'avaliacaoId'>,
  alvo: CreditoNoPainelData,
  mudados: readonly CampoDoCreditoLido[],
  valores: Valores,
  fonte: FonteDoValor,
  fundamento: string,
): Promise<string | null> {
  for (const campo of mudados) {
    const recusa = await gravarRevisao(props, {
      alvo: 'credito',
      chave: chaveDoCredito(alvo, campo),
      valor: (valores[campo] ?? '').trim(),
      documento_id: fonte.documentoId,
      pagina: Number(fonte.pagina),
      fundamento,
    });
    if (recusa !== null) return recusa;
  }
  return null;
}

/** As correções dos valores lidos e, depois, a linha; a primeira recusa pela versão volta. */
export async function gravarTudo(
  props: Pick<PropsDoConteudo, 'client' | 'caseId' | 'avaliacaoId'>,
  p: Gravavel & {
    edicao: EdicaoDoAlvo;
    fundamento: string;
    linha: string | null;
    resultado: ResultadoDaLinha | '';
  },
): Promise<string | null> {
  const { alvo, atual } = p.edicao;
  if (alvo !== undefined && atual !== null && p.mudados.length > 0) {
    const recusa = await gravarCorrecoesDoCredito(
      props,
      alvo,
      p.mudados,
      atual.valores,
      atual.fonte,
      p.fundamento,
    );
    if (recusa !== null) return recusa;
  }
  if (!p.gravaALinha || p.linha === null) return null;
  return gravarRevisao(props, {
    alvo: 'linha',
    chave: p.linha,
    valor: p.resultado,
    fundamento: p.fundamento,
  });
}
